import React, { useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { AmlCase, DecisionFormState, DecisionType } from './types';
import { DECISION_LABELS, DECISION_BUTTON_VARIANT } from './amlUtils';
import { primaryName } from './formatUser';
import { DECISION_REASONS } from '../../mocks/amlMocks';

interface Props {
  show: boolean;
  amlCase: AmlCase | null;
  onHide: () => void;
  onSubmit: (caseId: string, form: DecisionFormState) => Promise<void>;
}

const DECISION_TYPES: DecisionType[] = ['approve', 'reject', 'escalate', 'request_info'];

// Модальное окно принятия решения по AML-кейсу
const AmlDecisionModal: React.FC<Props> = ({ show, amlCase, onHide, onSubmit }) => {
  const [form, setForm] = useState<DecisionFormState>({
    decision: '',
    reasonCode: '',
    comment: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Фильтрация причин по выбранному типу решения
  const availableReasons = form.decision
    ? DECISION_REASONS.filter((r) => r.forDecision.includes(form.decision as DecisionType))
    : [];

  const handleSubmit = async () => {
    if (!amlCase) return;
    if (!form.decision) {
      setError('Выберите тип решения');
      return;
    }
    if (!form.reasonCode) {
      setError('Выберите причину решения');
      return;
    }
    if (!form.comment.trim()) {
      setError('Добавьте комментарий к решению');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit(amlCase.id, form);
      setForm({ decision: '', reasonCode: '', comment: '' });
      onHide();
    } catch (e: any) {
      setError(e?.message || 'Ошибка при сохранении решения');
    } finally {
      setSaving(false);
    }
  };

  const handleHide = () => {
    if (!saving) {
      setForm({ decision: '', reasonCode: '', comment: '' });
      setError(null);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Принять решение по кейсу{' '}
          <span className="text-muted fw-normal">{amlCase?.caseId || amlCase?.id}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {amlCase && (
          <div className="alert alert-light border mb-3 py-2">
            <strong>{primaryName(amlCase)}</strong>{' '}
            <span className="text-muted">{amlCase.userEmail || ''}</span>
            <br />
            <small className="text-muted">
              Скор риска: <strong>{amlCase.riskScore}</strong> |{' '}
              Правил сработало: <strong>{amlCase.triggeredRules.length || amlCase.rulesCount || 0}</strong>
            </small>
          </div>
        )}

        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {/* Тип решения */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Тип решения <span className="text-danger">*</span></Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {DECISION_TYPES.map((dt) => (
              <Button
                key={dt}
                size="sm"
                variant={
                  form.decision === dt
                    ? DECISION_BUTTON_VARIANT[dt]
                    : `outline-${DECISION_BUTTON_VARIANT[dt]}`
                }
                onClick={() => setForm({ ...form, decision: dt, reasonCode: '' })}
              >
                {DECISION_LABELS[dt]}
              </Button>
            ))}
          </div>
        </Form.Group>

        {/* Причина */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Причина решения <span className="text-danger">*</span></Form.Label>
          <Form.Select
            value={form.reasonCode}
            onChange={(e) => setForm({ ...form, reasonCode: e.target.value as any })}
            disabled={!form.decision}
          >
            <option value="">
              {form.decision ? 'Выберите причину...' : 'Сначала выберите тип решения'}
            </option>
            {availableReasons.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Комментарий */}
        <Form.Group className="mb-0">
          <Form.Label className="fw-semibold">
            Комментарий <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Опишите основание принятого решения..."
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
          />
          <Form.Text className="text-muted">
            Минимум 10 символов. Будет сохранено в истории кейса.
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={saving}>
          Отмена
        </Button>
        <Button
          variant={form.decision ? DECISION_BUTTON_VARIANT[form.decision as DecisionType] : 'primary'}
          onClick={handleSubmit}
          disabled={saving || !form.decision}
        >
          {saving ? (
            <><Spinner animation="border" size="sm" className="me-2" />Сохранение...</>
          ) : (
            form.decision ? DECISION_LABELS[form.decision as DecisionType] : 'Применить'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AmlDecisionModal;
